import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Visibility,
    Login,
    Logout,
    People
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useDailyTrackerStore from '@store/DailyTrackerStore';

// Type definitions - using backend variable names
interface AttendanceRecord {
    attendance_id: number;
    employee_id: number;
    employee_name: string;
    attendance_date: string;
    punch_in: string;
    punch_out: string | null;
    total_hours: string | null;
    daily_status: string;
    remarks: string | null;
}


const DailyTrackerPage: React.FC = () => {
    const {
        attendance,
        trackerCounts,
        loading,
        error,
        selectedDate,
        setSelectedDate,
        fetchDailyAttendance,
        fetchAttendanceSummary,
        fetchDailyTrackerCounts,
    } = useDailyTrackerStore();

    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null);
    const currentDate = useMemo(() => new Date().toISOString().split('T')[0], []);

    // Fetch stats only for current date on initial load
    useEffect(() => {
        fetchDailyTrackerCounts(currentDate);
    }, [fetchDailyTrackerCounts, currentDate]);

    // Fetch table data when date changes
    useEffect(() => {
        fetchDailyAttendance(selectedDate);
        fetchAttendanceSummary(selectedDate);
    }, [selectedDate, fetchDailyAttendance, fetchAttendanceSummary]);

    const filteredAttendance = attendance || [];

    const handleDateChange = (newDate: string) => {
        setSelectedDate(newDate);
    };

    const handleOpenViewDialog = (record: AttendanceRecord) => {
        setSelectedRecord(record);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedRecord(null);
    };

    if (loading && !attendance) {
        return (
            <Layout>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="xl">
                <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Daily Attendance Tracker
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Monitor daily attendance and employee activities
                            </Typography>
                        </Box>
                        <TextField
                            type="date"
                            label="Select Date"
                            value={selectedDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                max: new Date().toISOString().split('T')[0]
                            }}
                            size="small"
                            sx={{
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
                                }
                            }}
                        />
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Count Cards - Only show for current date */}
                    {selectedDate === currentDate && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, mb: 3 }}>
                            <Card sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white'
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                                Total Employees
                                            </Typography>
                                            <Typography variant="h4" fontWeight="bold">
                                                {trackerCounts?.total_employees || 0}
                                            </Typography>
                                        </Box>
                                        <People sx={{ fontSize: 48, opacity: 0.8 }} />
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card sx={{
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                color: 'white'
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                                Punched In
                                            </Typography>
                                            <Typography variant="h4" fontWeight="bold">
                                                {trackerCounts?.punched_in_count || 0}
                                            </Typography>
                                        </Box>
                                        <Login sx={{ fontSize: 48, opacity: 0.8 }} />
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card sx={{
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                color: 'white'
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                                                Punched Out
                                            </Typography>
                                            <Typography variant="h4" fontWeight="bold">
                                                {trackerCounts?.punched_out_count || 0}
                                            </Typography>
                                        </Box>
                                        <Logout sx={{ fontSize: 48, opacity: 0.8 }} />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    )}

                    {/* Attendance Table */}
                    <Card>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Employee</TableCell>
                                            <TableCell>Punch In</TableCell>
                                            <TableCell>Punch Out</TableCell>
                                            <TableCell>Working Hours</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredAttendance.map((record: AttendanceRecord) => (
                                            <TableRow 
                                                key={record.attendance_id} 
                                                hover
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {record.employee_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={record.daily_status === 'absent' ? '--' : (record.punch_in ? new Date(record.punch_in).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : 'Not punched')} 
                                                        size="small"
                                                        color="default"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={record.daily_status === 'absent' ? '--' : (record.punch_out ? new Date(record.punch_out).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '--')} 
                                                        size="small"
                                                        color={record.punch_out ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>{record.daily_status === 'absent' ? '--' : (record.total_hours || '--')}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="View Daily Update">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenViewDialog(record)}
                                                        >
                                                            <Visibility />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {filteredAttendance.length === 0 && (
                                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                    <Typography>No attendance records found for selected date</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Daily Update View Dialog */}
                    <Dialog 
                        open={openViewDialog} 
                        onClose={handleCloseViewDialog} 
                        maxWidth="md" 
                        fullWidth
                        PaperProps={{
                            sx: {
                                maxHeight: '80vh',
                                height: 'auto',
                                minHeight: '400px'
                            }
                        }}
                    >
                        <DialogTitle>
                            Daily Activity - {selectedRecord?.employee_name}
                        </DialogTitle>
                        <DialogContent 
                            sx={{ 
                                padding: 3,
                                overflowY: 'auto',
                                maxHeight: 'calc(80vh - 120px)' // Account for header and footer
                            }}
                        >
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Date: {selectedRecord?.attendance_date ? new Date(selectedRecord.attendance_date).toLocaleDateString() : 'N/A'}
                                </Typography>
                                
                                <Box sx={{ mt: 3, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom color="primary">
                                        Time Details
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Typography variant="body1">
                                            <strong>Punch In:</strong> {selectedRecord?.punch_in ? new Date(selectedRecord.punch_in).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }) : 'Not punched in'}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Punch Out:</strong> {selectedRecord?.punch_out ? new Date(selectedRecord.punch_out).toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' }) : 'Not punched out'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="h6" gutterBottom color="primary">
                                    Daily Update
                                </Typography>
                                <Box sx={{
                                    bgcolor: 'background.default',
                                    borderRadius: 2,
                                    p: 2.5,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    {selectedRecord?.remarks ? (
                                        <Box
                                            sx={{
                                                lineHeight: 1.7,
                                                color: 'text.primary',
                                                fontSize: '0.95rem',
                                                '& p': {
                                                    margin: '0.5em 0',
                                                    '&:first-of-type': { marginTop: 0 },
                                                    '&:last-child': { marginBottom: 0 }
                                                },
                                                '& ul, & ol': {
                                                    paddingLeft: '1.5rem',
                                                    margin: '0.5em 0',
                                                    '& li': {
                                                        marginBottom: '0.25em'
                                                    }
                                                },
                                                '& strong': {
                                                    fontWeight: 700
                                                },
                                                '& em': {
                                                    fontStyle: 'italic'
                                                }
                                            }}
                                            dangerouslySetInnerHTML={{ __html: selectedRecord.remarks }}
                                        />
                                    ) : (
                                        <Typography variant="body1" color="text.secondary">
                                            No daily update provided
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseViewDialog} variant="contained">Close</Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Container>
        </Layout>
    );
};

export default DailyTrackerPage;
